import { type Dispatch, type SetStateAction, useMemo, useState } from 'react'

import {
  type BasicCheckMerchantsResponse,
  type ProductObjectRequest,
} from '@redteclab/api/clients/bully'

import { type AlgoliaRecord } from '../../../algolia'
import {
  type NowGeneralContextValue,
  useIsNowEnabled,
  useNowApiV4SwrGetBasicCheckMutation,
  useNowGeneralContext,
} from '../../../now'
import { TENANT } from '../../../tenant'

export type ProductSearchResultItemNowDetails = {
  clearNowDataAndZipCode: () => void
  nowBasicCheckMutation: ReturnType<typeof useNowApiV4SwrGetBasicCheckMutation>
  nowCustomerJourneyEnabled: boolean
  nowDataPerProduct: NowDataPerProduct
  nowDataPerProductCurrentVariantResponse?: BasicCheckMerchantsResponse
  savedZipCode?: string
  triggerCheck: (upid: string, zipCode: string) => void
}

type NowDataPerProduct = Record<
  string,
  {
    checkedForZipCode: string
    fetchAt: number
    responseData: BasicCheckMerchantsResponse
  } | null
>

type HandleMutationSuccessParams = {
  nowCustomerJourneyEnabled: boolean
  nowGeneralContext: NowGeneralContextValue
  onMutationSuccess?: () => void
  products?: ProductObjectRequest[]
  response: BasicCheckMerchantsResponse
  setNowDataPerProduct: Dispatch<SetStateAction<NowDataPerProduct>>
  zipCode: string
}

const triggerCheckForProductMutation = ({
  trigger,
  upid,
  zipCode,
}: {
  trigger: ReturnType<typeof useNowApiV4SwrGetBasicCheckMutation>['trigger']
  upid: string
  zipCode: string
}): void => {
  void trigger({
    products: [
      {
        nowProduct: 0,
        priceCentsGross: 0,
        quantity: 1,
        upid,
      },
    ],
    zipCode,
  })
}

const handleMutationSuccess = ({
  nowCustomerJourneyEnabled,
  nowGeneralContext,
  onMutationSuccess,
  products,
  response,
  setNowDataPerProduct,
  zipCode,
}: HandleMutationSuccessParams): void => {
  const upid = products?.[0]?.upid
  if (!upid) {
    return
  }

  setNowDataPerProduct((prev) => ({
    ...prev,
    [upid]: {
      checkedForZipCode: zipCode,
      fetchAt: Date.now(),
      responseData: response,
    },
  }))
  // update general zip support.
  nowGeneralContext.updateZipSupportInfo({
    checkedAt: Date.now(),
    inDeliveryRegion: response.zipCodeInDeliveryRegion,
    zip: zipCode,
  })

  if (nowCustomerJourneyEnabled) {
    onMutationSuccess?.()
  }
}

export const useProductSearchResultItemNowDetails = ({
  currentVariant,
  onMutationSuccess,
  tenant,
}: {
  currentVariant: AlgoliaRecord
  onMutationSuccess?: () => void
  tenant: TENANT
}): ProductSearchResultItemNowDetails => {
  const [nowDataPerProduct, setNowDataPerProduct] = useState<NowDataPerProduct>(
    {},
  )
  const isNowEnabled = useIsNowEnabled()
  const nowGeneralContext = useNowGeneralContext()
  const nowCustomerJourneyEnabled: boolean =
    (!currentVariant.prescriptionMedicine &&
      isNowEnabled &&
      currentVariant.best_offer &&
      tenant === TENANT.COM) ??
    false

  const nowBasicCheckMutation = useNowApiV4SwrGetBasicCheckMutation({
    onSuccess: (response, zipCode, products) => {
      handleMutationSuccess({
        nowCustomerJourneyEnabled,
        nowGeneralContext,
        onMutationSuccess,
        products,
        response,
        setNowDataPerProduct,
        zipCode,
      })
    },
    swrKeyNonce: `productSearchResult-${currentVariant.upid}`,
  })

  return useMemo(() => {
    return {
      clearNowDataAndZipCode: (): void => {
        setNowDataPerProduct({})
        nowGeneralContext.clearZipSupportInfo()
        if (nowCustomerJourneyEnabled) {
          onMutationSuccess?.()
        }
      },
      nowBasicCheckMutation,
      nowCustomerJourneyEnabled,
      nowDataPerProduct,
      nowDataPerProductCurrentVariantResponse:
        nowDataPerProduct[currentVariant.upid]?.responseData,
      triggerCheck: (upid: string, zipCode: string): void => {
        triggerCheckForProductMutation({
          trigger: nowBasicCheckMutation.trigger,
          upid,
          zipCode,
        })
      },
    }
  }, [
    nowCustomerJourneyEnabled,
    nowBasicCheckMutation,
    currentVariant.upid,
    nowDataPerProduct,
    nowGeneralContext,
    onMutationSuccess,
  ])
}
